'use client';

import {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import { useMutation } from '@apollo/client/react';
import { BehaviorSubject, Subject } from 'rxjs';

import { createAudioProcessor, useVisibilityChange } from '@/shared-utilities';
import {
  ProjectType,
  GET_ASSEMBLYAI_TOKEN as SURVEY_GET_ASSEMBLYAI_TOKEN,
  ProjectMode,
} from '@/survey-graphql';

import {
  HIDDEN_RESTART_THRESHOLD_MS,
  SPEECH_TO_TEXT_TOKEN_REQUEST,
} from './shared-survey-project-response-mode.consts';
import ProjectResponseContext, {
  ProjectResponseModeContextValue,
} from './shared-survey-project-response-mode.context';
import {
  buildListeningStateChangedEvent,
  buildResponseModeChangedEvent,
  buildSttAudioConstraints,
  buildTranscriptChangedEvent,
  createSttAudioSource,
  resolveSkipLocalMic,
  shouldEmitTranscriptChange,
} from './shared-survey-project-response-mode.helpers';
import {
  ProjectResponseModeEvent,
  ResponseMode,
  ResponseVoiceModeProps,
  VoiceCommandEvent,
} from './shared-survey-project-response-mode.types';
import { createSpeechToTextWorker } from './shared-survey-speech-to-text-worker';
import { buildAccumulatedTranscript } from './shared-survey-transcript-accumulation.helpers';

const keepFirst = <T>(state: T): T => state;

export function useProjectResponseModeProvider(
  getAssemblyAiTokenDocument: typeof SURVEY_GET_ASSEMBLYAI_TOKEN | undefined,
  projectMode: ProjectMode | null = ProjectMode.Chat,
  projectType: ProjectType | null = ProjectType.Survey,
  preferredDeviceId?: string
) {
  const workerRef = useRef<Worker | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastFinalTranscriptRef = useRef<string | null>('');
  const currentTranscriptRef = useRef<string>('');
  const lastEmittedTranscriptRef = useRef<string | null>(null);
  const processorRef = useRef<AudioWorkletNode | ScriptProcessorNode | null>(
    null
  );
  const processorCleanupRef = useRef<(() => void) | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const startDateOfTranscriptRef = useRef<number | null>(null);
  const lastDateOfTranscriptRef = useRef<number | null>(null);
  const sampleRateRef = useRef<number>(24000);
  const isMountedRef = useRef(true);
  const isListeningRef = useRef(false);
  const isStartingRef = useRef(false);
  const skipLocalMicRef = useRef(false);
  const hiddenAtRef = useRef<number | null>(null);

  const [responseMode, setResponseMode] = useState<ResponseMode>('text');
  const [responseVoice, setResponseVoice] = useState<
    Omit<
      ResponseVoiceModeProps,
      'audioLevelObservable' | 'eventsObservable' | 'audioDataObservable'
    >
  >({
    isListening: false,
    isStarting: false,
    currentTranscript: '',
    error: null,
  });
  const [audioLevelObservable] = useReducer(
    keepFirst,
    undefined,
    () => new BehaviorSubject<number>(0.5)
  );
  const [eventsObservable] = useReducer(
    keepFirst,
    undefined,
    () => new Subject<ProjectResponseModeEvent>()
  );
  const [audioDataObservable] = useReducer(
    keepFirst,
    undefined,
    () =>
      new Subject<{
        data: Int16Array;
        date: number;
      }>()
  );

  const [
    getAssemblyAIToken,
    { loading: tokenLoading, data: assemblyAITokenData },
  ] = useMutation(getAssemblyAiTokenDocument ?? SURVEY_GET_ASSEMBLYAI_TOKEN, {
    onError: (error) => {
      const errorMessage = `Failed to get AssemblyAI token: ${error.message}`;
      setResponseVoice((prev) => ({ ...prev, error: errorMessage }));
    },
  });

  const setError = useCallback(
    (error: string | null) => {
      setResponseVoice((prev) => ({ ...prev, error }));
    },
    [setResponseVoice]
  );

  const setIsStarting = useCallback(
    (value: boolean) => {
      isStartingRef.current = value;
      setResponseVoice((prev) => ({ ...prev, isStarting: value }));
    },
    [setResponseVoice]
  );

  const setIsListening = useCallback(
    (isListening: boolean) => {
      const changed = isListeningRef.current !== isListening;
      setResponseVoice((prev) => ({ ...prev, isListening }));
      isListeningRef.current = isListening;
      if (changed) {
        eventsObservable.next(
          buildListeningStateChangedEvent(
            isListening,
            currentTranscriptRef.current
          )
        );
      }
    },
    [eventsObservable, setResponseVoice]
  );

  const emitTranscriptChanged = useCallback(
    (transcript: string, isFinal: boolean) => {
      if (
        !shouldEmitTranscriptChange(
          lastEmittedTranscriptRef.current ?? '',
          transcript
        )
      ) {
        return;
      }
      lastEmittedTranscriptRef.current = transcript;
      eventsObservable.next(buildTranscriptChangedEvent(transcript, isFinal));
    },
    [eventsObservable]
  );

  const setCurrentTranscript = useCallback(
    (currentTranscript: string) => {
      if (!currentTranscript) {
        lastFinalTranscriptRef.current = '';
        startDateOfTranscriptRef.current = null;
        lastDateOfTranscriptRef.current = null;
      }
      currentTranscriptRef.current = currentTranscript;
      setResponseVoice((prev) => ({ ...prev, currentTranscript }));
      emitTranscriptChanged(currentTranscript, true);
    },
    [emitTranscriptChanged, setResponseVoice]
  );

  const ingestTranscript = useCallback(
    (transcript: string, isFinal: boolean) => {
      const newTranscript = buildAccumulatedTranscript(
        lastFinalTranscriptRef.current ?? '',
        transcript,
        isFinal
      );
      currentTranscriptRef.current = newTranscript;
      setResponseVoice((prev) => ({
        ...prev,
        currentTranscript: newTranscript,
      }));
      emitTranscriptChanged(newTranscript, isFinal);
      if (isFinal) {
        lastFinalTranscriptRef.current = newTranscript;
      }
    },
    [emitTranscriptChanged, setResponseVoice]
  );

  const cleanup = useCallback(
    (reset = false) => {
      if (processorCleanupRef.current) {
        processorCleanupRef.current();
        processorCleanupRef.current = null;
      }

      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }

      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }

      if (audioContextRef.current) {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          track.stop();
        });
        streamRef.current = null;
      }

      if (reset) {
        setCurrentTranscript('');
      }
    },
    [setCurrentTranscript]
  );

  useEffect(() => {
    isMountedRef.current = true;

    void (async function () {
      try {
        const worker = await createSpeechToTextWorker();
        workerRef.current = worker;

        /* oxlint-disable unicorn/prefer-add-event-listener */
        workerRef.current.onmessage = (e) => {
          const { type, payload } = e.data;

          switch (type) {
            case 'TRANSCRIPT':
              {
                if (!isMountedRef.current) break;
                const { transcript, isFinal, date } = payload;
                ingestTranscript(transcript, isFinal);
                if (!startDateOfTranscriptRef.current) {
                  startDateOfTranscriptRef.current = date;
                }
                lastDateOfTranscriptRef.current = date;
              }
              break;
            case 'VOICE_COMMAND':
              {
                if (!isMountedRef.current || !isListeningRef.current) {
                  return;
                }
                const {
                  type: commandType,
                  transcript,
                  confidence,
                  timestamp,
                  triggerWord,
                } = payload;
                const voiceCommandEvent: VoiceCommandEvent = {
                  type: commandType,
                  transcript,
                  confidence,
                  timestamp,
                  triggerWord,
                };
                eventsObservable.next(voiceCommandEvent);
              }
              break;
            case 'ERROR':
              if (!isMountedRef.current) break;
              setError(payload.error);
              setIsStarting(false);
              setIsListening(false);
              cleanup();
              break;
            case 'STATUS':
              break;
            case 'LISTENING_STARTED':
              if (!isMountedRef.current) break;
              setIsListening(true);
              setIsStarting(false);
              setError(null);
              break;
            case 'LISTENING_STOPPED':
              if (!isMountedRef.current) break;
              setIsListening(false);
              cleanup();
              break;
          }
        };

        workerRef.current.onerror = () => {
          setError('STT Worker error');
        };

        workerRef.current.onmessageerror = () => {
          setError('STT Worker message error');
        };
        /* oxlint-enable unicorn/prefer-add-event-listener */
      } catch {
        setError('Failed to initialize STT worker');
      }
    })();

    return () => {
      isMountedRef.current = false;
      if (workerRef.current) {
        workerRef.current.terminate();
        workerRef.current = null;
      }
      cleanup();
    };
  }, [
    cleanup,
    ingestTranscript,
    setError,
    setIsListening,
    setIsStarting,
    eventsObservable,
  ]);

  const { isListening, isStarting, currentTranscript, error } = responseVoice;

  const setupAudioProcessing = async (stream: MediaStream) => {
    try {
      const { audioContext, source } = createSttAudioSource(stream);
      const sampleRate = audioContext.sampleRate;

      sampleRateRef.current = sampleRate;

      audioContextRef.current = audioContext;

      const { processor, cleanup: processorCleanup } =
        await createAudioProcessor(audioContext, {
          onAudioLevel: (level) => {
            audioLevelObservable.next(level);
          },
          onAudioData: (data, timestamp) => {
            const copyForObservable = new Int16Array(data.byteLength / 2);
            copyForObservable.set(new Int16Array(data));
            audioDataObservable.next({
              data: copyForObservable,
              date: timestamp,
            });

            if (workerRef.current) {
              workerRef.current.postMessage(
                {
                  id: `audio_${timestamp}`,
                  type: 'AUDIO_DATA',
                  payload: data,
                },
                [data]
              );
            }
          },
        });

      processorCleanupRef.current = processorCleanup;
      processorRef.current = processor;
      sourceRef.current = source;

      source.connect(processor);
      processor.connect(audioContext.destination);

      if (tokenLoading) {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (processorRef.current) {
          processorRef.current.disconnect();
          processorRef.current = null;
        }
        if (audioContextRef.current) {
          void audioContextRef.current.close();
          audioContextRef.current = null;
        }
        setIsStarting(false);
        return;
      }
      let assemblyAIToken: string;
      const expiresAt = assemblyAITokenData?.assemblyAIToken?.expiresAt
        ? new Date(assemblyAITokenData?.assemblyAIToken?.expiresAt)
        : null;
      if (
        assemblyAITokenData?.assemblyAIToken?.token &&
        expiresAt &&
        expiresAt > new Date()
      ) {
        assemblyAIToken = assemblyAITokenData.assemblyAIToken.token;
      } else {
        const { data } = await getAssemblyAIToken({
          variables: {
            input: SPEECH_TO_TEXT_TOKEN_REQUEST,
          },
        });

        if (!isMountedRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          if (processorCleanupRef.current) {
            processorCleanupRef.current();
          }
          if (sourceRef.current) {
            sourceRef.current.disconnect();
          }
          if (processorRef.current) {
            processorRef.current.disconnect();
          }
          if (audioContextRef.current) {
            void audioContextRef.current.close();
          }
          streamRef.current = null;
          return;
        }
        if (!data?.assemblyAIToken?.token) {
          setError(
            'Failed to get AssemblyAI token from GraphQL - no token in response'
          );
          stream.getTracks().forEach((track) => track.stop());
          if (processorCleanupRef.current) {
            processorCleanupRef.current();
          }
          if (sourceRef.current) {
            sourceRef.current.disconnect();
          }
          if (processorRef.current) {
            processorRef.current.disconnect();
          }
          if (audioContextRef.current) {
            void audioContextRef.current.close();
          }
          streamRef.current = null;
          setIsStarting(false);
          return;
        }

        const { token } = data.assemblyAIToken;
        assemblyAIToken = token;
      }

      const message = {
        id: `start_${Date.now()}`,
        type: 'START_LISTENING',
        payload: {
          token: assemblyAIToken,
          sampleRate: sampleRate ?? 44100,
          format: 'pcm16',
          channels: 1,
        },
      };

      if (
        isMountedRef.current &&
        workerRef.current &&
        !isListeningRef.current
      ) {
        /* oxlint-disable unicorn/require-post-message-target-origin */
        workerRef.current.postMessage(message);
        /* oxlint-enable unicorn/require-post-message-target-origin */
      } else if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        if (processorCleanupRef.current) {
          processorCleanupRef.current();
        }
        if (sourceRef.current) {
          sourceRef.current.disconnect();
        }
        if (processorRef.current) {
          processorRef.current.disconnect();
        }
        if (audioContextRef.current) {
          void audioContextRef.current.close();
        }
        streamRef.current = null;
      }
    } catch (error) {
      setError(
        `Failed to set up audio processing: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      );
      stream.getTracks().forEach((track) => track.stop());
      if (processorCleanupRef.current) {
        processorCleanupRef.current();
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (processorRef.current) {
        processorRef.current.disconnect();
      }
      if (audioContextRef.current) {
        void audioContextRef.current.close();
      }
      streamRef.current = null;
      setIsStarting(false);
    }
  };

  const stopListening = async (reset = false) => {
    if (workerRef.current) {
      const message = {
        id: `stop_${Date.now()}`,
        type: 'STOP_LISTENING',
      };
      /* oxlint-disable unicorn/require-post-message-target-origin */
      workerRef.current.postMessage(message);
      /* oxlint-enable unicorn/require-post-message-target-origin */
    }
    setIsListening(false);
    cleanup(reset);
    setIsStarting(false);
  };

  const sendForceEndpoint = () => {
    if (workerRef.current) {
      /* oxlint-disable unicorn/require-post-message-target-origin */
      workerRef.current.postMessage({
        type: 'SEND_FORCE_ENDPOINT',
      });
      /* oxlint-enable unicorn/require-post-message-target-origin */
    }
  };

  const startListening = async (options?: { skipLocalMic?: boolean }) => {
    if (isListeningRef.current || isStartingRef.current) {
      return;
    }
    setIsStarting(true);

    const skipLocalMic = resolveSkipLocalMic(
      options?.skipLocalMic,
      skipLocalMicRef.current
    );
    skipLocalMicRef.current = skipLocalMic;

    // Mobile UT inner page: voice input arrives via GQL subscription, not local mic
    if (skipLocalMic) {
      isListeningRef.current = true;
      setIsListening(true);
      setResponseMode('voice');
      setIsStarting(false);
      return;
    }

    if (!workerRef.current) {
      const errorMessage = 'STT Worker not available';
      setError(errorMessage);
      setIsStarting(false);
      return;
    }

    try {
      setError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: buildSttAudioConstraints(preferredDeviceId),
      });

      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;

      await setupAudioProcessing(stream);
    } catch (error) {
      if (!isMountedRef.current) return;
      const errorMessage = `Failed to start voice recognition: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`;
      setError(errorMessage);
      setIsStarting(false);
      void stopListening();
      setIsListening(false);
    }
  };

  // Backgrounded tabs may suspend the STT AudioContext and let AssemblyAI
  // close the idle WebSocket. On return after a non-trivial hidden period
  // the worker still believes it's listening but no audio flows, so the
  // session must be rebuilt to restore the mic → worker → AssemblyAI pipeline.
  useVisibilityChange((visibilityState) => {
    if (visibilityState === 'hidden') {
      if (isListeningRef.current || isStartingRef.current) {
        hiddenAtRef.current = Date.now();
      }
      return;
    }

    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {
        // ignore
      });
    }

    const hiddenAt = hiddenAtRef.current;
    hiddenAtRef.current = null;
    if (
      hiddenAt !== null &&
      Date.now() - hiddenAt >= HIDDEN_RESTART_THRESHOLD_MS &&
      (isListeningRef.current || isStartingRef.current)
    ) {
      void (async () => {
        try {
          await stopListening(false);
          await new Promise((resolve) => setTimeout(resolve, 150));
          await startListening();
        } catch {
          // useHandsFreeListeningRecovery will retry
        }
      })();
    }
  });

  const handleToggleListening = () => {
    if (isListening) {
      void stopListening();
    } else {
      void startListening();
    }
  };

  const applyResponseMode = (next: ResponseMode) => {
    const prev = responseMode;
    if (prev !== next) {
      eventsObservable.next(
        buildResponseModeChangedEvent(prev, next, currentTranscriptRef.current)
      );
    }
    setResponseMode(next);
  };

  const handleToggleResponseMode = () => {
    const newMode = responseMode === 'text' ? 'voice' : 'text';
    applyResponseMode(newMode);
    if (newMode === 'voice') {
      void startListening();
    } else {
      void stopListening(true);
    }
  };

  const manuallyEditTranscript = useCallback(
    (transcript: string) => {
      setCurrentTranscript(transcript);
      lastFinalTranscriptRef.current = transcript;
      lastDateOfTranscriptRef.current = Date.now();
    },
    [setCurrentTranscript]
  );

  const contextValue: ProjectResponseModeContextValue = {
    projectMode: projectMode ?? ProjectMode.Chat,
    projectType: projectType ?? ProjectType.Survey,
    responseMode,
    toggleResponseMode: handleToggleResponseMode,
    setResponseMode: applyResponseMode,
    isListening,
    isStarting,
    currentTranscript,
    setCurrentTranscript,
    ingestTranscript,
    error,
    audioLevelObservable: audioLevelObservable,
    eventsObservable: eventsObservable,
    audioDataObservable: audioDataObservable,
    startDateOfTranscript: () => startDateOfTranscriptRef.current,
    lastDateOfTranscript: () => lastDateOfTranscriptRef.current,
    manuallyEditTranscript,
    getSampleRate: () => sampleRateRef.current,
    startListening,
    stopListening,
    handleToggleListening,
    tokenLoading,
    sendForceEndpoint,
  };

  return contextValue;
}

export function useProjectResponseModeContext() {
  const context = useContext(ProjectResponseContext);
  if (context === undefined) {
    throw new Error(
      'useProjectResponseModeContext must be used within a ProjectResponseModeProvider'
    );
  }
  return context;
}
